import React, { useMemo } from 'react'
import { List } from '@mui/material'
import { useSidebar } from '../../shared/components/Sidebar/SidebarProvider'
import { AnySidebarSection, SidebarNavigationItem, SidebarSection } from '../../shared/types/navigation'
import { LAYOUT_SPACING } from '../../constants/layout'
import { SidebarMenuItem } from './SidebarMenuItem'

/**
 * Props for SidebarMenuList
 */
export interface SidebarMenuListProps {
  navigationItems: SidebarNavigationItem[]
  /** Section IDs that should show the live activity badge */
  liveActivitySectionIds?: AnySidebarSection[]
  /** Section IDs that should show the "soon" blinking badge */
  soonActivitySectionIds?: AnySidebarSection[]
  /** Section IDs that should show the green "New" badge */
  newSectionIds?: AnySidebarSection[]
  notificationCountBySection?: Partial<Record<SidebarSection, number>>
  isAdmin: boolean
  onItemClick: (item: SidebarNavigationItem) => void
}

/**
 * SidebarMenuList - Container for all sidebar menu items
 *
 * Features:
 * - Filters navigation items based on user role
 * - Renders list with proper spacing
 * - Manages active state for each item
 */
export const SidebarMenuList: React.FC<SidebarMenuListProps> = ({
  navigationItems,
  liveActivitySectionIds,
  soonActivitySectionIds,
  newSectionIds,
  notificationCountBySection,
  isAdmin,
  onItemClick
}) => {
  const { activeSection } = useSidebar()

  // Filter navigation items based on user role and visibility settings
  const filteredNavigationItems = useMemo(() => {
    return navigationItems.filter((item) => {
      if (item.adminOnly && !isAdmin) {
        return false
      }
      if (item.hideInSidebar) {
        return false
      }
      return true
    })
  }, [navigationItems, isAdmin])

  return (
    <List
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: `${LAYOUT_SPACING.SIDEBAR_LIST_GAP}px`,
        padding: 0
      }}
    >
      {filteredNavigationItems.map((item) => {
        const isActive = activeSection === item.id && !item.disabled
        const showLiveBadge = liveActivitySectionIds?.includes(item.id) ?? false
        const showSoonBadge = !showLiveBadge && (soonActivitySectionIds?.includes(item.id) ?? false)
        const showNewBadge = newSectionIds?.includes(item.id) ?? false
        const notificationCount = notificationCountBySection?.[item.id as SidebarSection] ?? 0

        return (
          <SidebarMenuItem
            key={item.id}
            item={item}
            isActive={isActive}
            showLiveBadge={showLiveBadge}
            showSoonBadge={showSoonBadge}
            showNewBadge={showNewBadge}
            notificationCount={notificationCount}
            onClick={onItemClick}
          />
        )
      })}
    </List>
  )
}
